import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type FC,
	type ReactElement,
	type ReactNode
} from "react";
import {PointsEarnedFloater} from "@/app2/shared/components/PointsEarnedFloater";
import useDevCycleSdk from "@/app2/shared/hooks/useDevCycleSdk";

type PointsEarnedFloaterContextValue = {
	triggerPointsFloater: (points: number) => void;
};

const PointsEarnedFloaterContext = createContext<PointsEarnedFloaterContextValue | undefined>(undefined);

export const useTriggerPointsEarnedFloater = (): PointsEarnedFloaterContextValue => {
	const ctx = useContext(PointsEarnedFloaterContext);
	if (!ctx) {
		throw new Error("useTriggerPointsEarnedFloater must be used within PointsEarnedFloaterProvider");
	}
	return ctx;
};

interface PointsEarnedFloaterProviderProps {
	children: ReactElement | ReactNode;
}

/**
 * Renders {@link PointsEarnedFloater} when triggered via {@link useTriggerPointsEarnedFloater}.
 */
export const PointsEarnedFloaterProvider: FC<PointsEarnedFloaterProviderProps> = ({children}) => {
	const {leaderboards: leaderboardsEnabled} = useDevCycleSdk();
	const [floater, setFloater] = useState<{id: number; points: number} | null>(null);

	const triggerPointsFloater = useCallback(
		(points: number) => {
			if (!leaderboardsEnabled) {
				return;
			}
			if (!Number.isFinite(points) || points <= 0) {
				return;
			}
			setFloater({id: Date.now(), points: Math.floor(points)});
		},
		[leaderboardsEnabled]
	);

	const handleAnimationEnd = useCallback(() => {
		setFloater(null);
	}, []);

	const value = useMemo(() => ({triggerPointsFloater}), [triggerPointsFloater]);

	return (
		<PointsEarnedFloaterContext.Provider value={value}>
			{children}
			{floater != null && (
				<PointsEarnedFloater
					key={floater.id}
					points={floater.points}
					onAnimationEnd={handleAnimationEnd}
				/>
			)}
		</PointsEarnedFloaterContext.Provider>
	);
};
